import { supabase } from '@/services/supabase/supabase';
import { getStoriesFromCache } from '@/utils/query-cache-utils';

/**
 * API service for the progress tracker feature
 */

// Types
export interface WritingSession {
  id: string;
  user_id: string;
  tenant_id: string;
  start_time: string;
  end_time: string;
  duration: number;
  chapter_id: number | null;
  story_id: number;
  project_id?: number | null;
  created_at: string;
  updated_at: string;
  story_name?: string;
  chapter_name?: string;
}

export interface WordCountHistory {
  id: string;
  user_id: string;
  tenant_id: string;
  date: string;
  story_id: number;
  chapter_id: number | null;
  word_count: number;
  daily_change: number;
  created_at: string;
  updated_at: string;
}

export interface WritingGoal {
  id: string;
  user_id: string;
  tenant_id: string;
  name: string;
  deadline: string;
  story_id: number | null;
  chapter_id: number | null;
  project_id: number | null;
  completed: boolean;
  completed_date: string | null;
  start_date: string;
  created_at: string;
  updated_at: string;
  story_name?: string; // Added for UI display
}

export interface WritingAchievement {
  id: string;
  user_id: string;
  tenant_id: string;
  achievement_type: string;
  date_earned: string;
  description: string;
  story_id: number | null;
  created_at: string;
}

export interface WritingStats {
  totalSessions: number;
  totalTimeSpent: number; // in minutes
  avgSessionDuration: number; // in minutes
  firstSessionDate: string | null;
  activeDays: number; // total number of days with writing activity
  avgTimePerActiveDay: number; // average minutes per active day
  dailyConsistency: number; // percentage of days with writing activity since first session
}

export interface DailyWritingActivity {
  date: string; // in YYYY-MM-DD format
  sessionCount: number;
  totalMinutes: number;
}

// API Functions

/**
 * Get writing sessions for a story or project
 */
export async function getWritingSessions({ projectId, storyId }: { projectId: string, storyId?: string }) {
  try {
    let query = supabase
      .from('writing_sessions')
      .select('*')
      .order('start_time', { ascending: false });

    if (storyId) {
      query = query.eq('story_id', parseInt(storyId));
    } else if (projectId) {
      // Filter directly by project_id
      query = query.eq('project_id', parseInt(projectId));
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching writing sessions:', error);
    return [];
  }
}



/**
 * Create a new writing session
 */
export async function createWritingSession({
  storyId,
  chapterId,
  projectId,
  date,
  duration,
  startTime,
  endTime
}: {
  storyId: string | number;
  chapterId?: string | number;
  projectId?: string | number;
  date?: Date;
  duration: number;
  startTime?: Date;
  endTime?: Date;
}): Promise<WritingSession | null> {
  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Convert string IDs to numbers if needed
    const numericStoryId = typeof storyId === 'string' ? parseInt(storyId) : storyId;
    const numericChapterId = chapterId ?
      (typeof chapterId === 'string' ? parseInt(chapterId) : chapterId) :
      null;
    const numericProjectId = projectId ?
      (typeof projectId === 'string' ? parseInt(projectId) : projectId) :
      null;

    // If projectId is not provided but storyId is, get the projectId from the story
    let resolvedProjectId = numericProjectId;
    if (!resolvedProjectId && numericStoryId) {
      try {
        const { data: storyData } = await supabase
          .from('stories')
          .select('project_id')
          .eq('id', numericStoryId)
          .single();

        if (storyData) {
          resolvedProjectId = storyData.project_id;
        }
      } catch (err) {
        console.error('Error fetching project_id from story:', err);
      }
    }

    // Calculate times based on provided values or defaults
    const sessionDate = date || new Date();
    const sessionStartTime = startTime || sessionDate;
    const sessionEndTime = endTime || new Date(sessionStartTime.getTime() + duration * 1000); // Convert duration to milliseconds

    const { data, error } = await supabase
      .from('writing_sessions')
      .insert({
        story_id: numericStoryId,
        chapter_id: numericChapterId,
        project_id: resolvedProjectId,
        start_time: sessionStartTime.toISOString(),
        end_time: sessionEndTime.toISOString(),
        duration: duration,
        words_added: 0,
        words_deleted: 0
        // user_id and tenant_id will be set by the database trigger
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating writing session:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error creating writing session:', error);
    return null;
  }
}

/**
 * Get word count history for a story or project
 */
export async function getWordCountHistory({
  projectId,
  storyId,
  timeframe = 'week'
}: {
  projectId: string,
  storyId?: string,
  timeframe?: 'week' | 'month' | 'year'
}) {
  try {
    // Calculate date range based on timeframe
    const now = new Date();
    let startDate = new Date();

    if (timeframe === 'week') {
      startDate.setDate(now.getDate() - 7);
    } else if (timeframe === 'month') {
      startDate.setMonth(now.getMonth() - 1);
    } else {
      startDate.setFullYear(now.getFullYear() - 1);
    }

    let query = supabase
      .from('word_count_history')
      .select('*')
      .gte('date', startDate.toISOString().split('T')[0])
      .order('date', { ascending: false });

    if (storyId) {
      query = query.eq('story_id', parseInt(storyId));
    } else if (projectId) {
      // Fetch stories for this project using our cache utility
      try {
        const stories = await getStoriesFromCache(projectId);
        if (stories && stories.length > 0) {
          const storyIds = stories.map(story => story.id);
          query = query.in('story_id', storyIds);
        }
      } catch (err) {
        console.error('Error fetching stories for project:', err);
      }
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching word count history:', error);
    return [];
  }
}

/**
 * Create or update word count history for a day
 */
export async function updateWordCountHistory({
  date,
  story_id,
  chapter_id,
  word_count,
  daily_change
}: {
  date: string | Date;
  story_id: string | number;
  chapter_id?: string | number;
  word_count: number;
  daily_change?: number;
}): Promise<WordCountHistory | null> {
  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Convert string IDs to numbers if needed
    const numericStoryId = typeof story_id === 'string' ? parseInt(story_id) : story_id;
    const numericChapterId = chapter_id ?
      (typeof chapter_id === 'string' ? parseInt(chapter_id as string) : chapter_id) :
      null;

    // Ensure we have a proper date string
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];

    // Check if an entry already exists for this date and story
    const { data: existingEntries, error: fetchError } = await supabase
      .from('word_count_history')
      .select('id, word_count')
      .eq('date', dateStr)
      .eq('story_id', numericStoryId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error checking word count history:', fetchError);
      throw fetchError;
    }

    const actualDailyChange = daily_change || word_count - (existingEntries?.word_count || 0);

    if (existingEntries) {
      // Update existing entry
      const { data, error } = await supabase
        .from('word_count_history')
        .update({
          word_count: word_count,
          daily_change: actualDailyChange
        })
        .eq('id', existingEntries.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating word count history:', error);
        throw error;
      }

      return data;
    } else {
      // Create new entry
      const { data, error } = await supabase
        .from('word_count_history')
        .insert({
          date: dateStr,
          story_id: numericStoryId,
          chapter_id: numericChapterId,
          word_count: word_count,
          daily_change: actualDailyChange
          // user_id and tenant_id will be set by the database trigger
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating word count history:', error);
        throw error;
      }

      return data;
    }
  } catch (error) {
    console.error('Error updating word count history:', error);
    return null;
  }
}

/**
 * Get writing goals for a user, optionally filtered by project
 */
export async function getGoals(projectId?: string | number) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('writer_goals')
      .select('*')
      .order('deadline', { ascending: true });

    // Filter by project if provided
    if (projectId) {
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;
      query = query.eq('project_id', numericProjectId);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Enrich goals with story names if they have story_id
    if (data && data.length > 0) {
      const storyIds = data
        .filter(goal => goal.story_id)
        .map(goal => goal.story_id);

      if (storyIds.length > 0) {
        const { data: storiesData, error: storiesError } = await supabase
          .from('stories')
          .select('id, name')
          .in('id', storyIds);

        if (!storiesError && storiesData) {
          const storyMap = Object.fromEntries(
            storiesData.map(story => [story.id, story.name])
          );

          // Add story_name to each goal
          data.forEach(goal => {
            if (goal.story_id && storyMap[goal.story_id]) {
              goal.story_name = storyMap[goal.story_id];
            }
          });
        }
      }
    }

    return data as WritingGoal[];
  } catch (error) {
    console.error('Error fetching goals:', error);
    return null;
  }
}

/**
 * Create a new writing goal
 */
export async function createWritingGoal(goal: {
  name: string;
  deadline: string;
  story_id?: number | null;
  chapter_id?: number | null;
  projectId?: number | null;
}) {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Convert string IDs to numbers if needed
    const storyId = goal.story_id ?
      (typeof goal.story_id === 'string' ? parseInt(goal.story_id as string) : goal.story_id) :
      null;

    const chapterId = goal.chapter_id ?
      (typeof goal.chapter_id === 'string' ? parseInt(goal.chapter_id as string) : goal.chapter_id) :
      null;

    const projectId = goal.projectId ?
      (typeof goal.projectId === 'string' ? parseInt(goal.projectId as string) : goal.projectId) :
      87; // Default to 87 if not provided

    const { data, error } = await supabase
      .from('writer_goals')
      .insert({
        name: goal.name,
        deadline: goal.deadline,
        story_id: storyId,
        chapter_id: chapterId,
        project_id: projectId,
        completed: false,
        start_date: new Date().toISOString()
        // tenant_id and user_id will be set by RLS & database triggers
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating writing goal:', error);
    return null;
  }
}

/**
 * Update an existing writing goal
 */
export async function updateWritingGoal(id: string, updates: Partial<WritingGoal>) {
  try {
    const { data, error } = await supabase
      .from('writer_goals')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating writing goal:', error);
    return null;
  }
}

/**
 * Get writing achievements for a user
 */
export async function getWritingAchievements({ userId }: { userId: string }) {
  try {
    const { data, error } = await supabase
      .from('writing_achievements')
      .select('*')
      .eq('user_id', userId)
      .order('date_earned', { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching writing achievements:', error);
    return [];
  }
}

/**
 * Get writing streak for the current user
 * A streak is defined as consecutive days with writing sessions
 */
export const getWritingStreak = async (userId?: string, projectId?: string | number): Promise<number> => {
  try {
    // If userId is not provided, get it from the session
    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id;

      if (!userId) {
        console.error('No user logged in');
        return 0;
      }
    }

    // Get all writing sessions for this user, filtered by project if provided
    let query = supabase
      .from('writing_sessions')
      .select('start_time')
      .eq('user_id', userId)
      .order('start_time', { ascending: false });

    // Add project filter if provided
    if (projectId) {
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;
      query = query.eq('project_id', numericProjectId);
    }

    const { data: sessionsData, error } = await query;

    if (error) {
      console.error('Error fetching session data:', error);
      return 0;
    }

    if (!sessionsData || sessionsData.length === 0) {
      return 0;
    }

    // Extract dates from sessions and format to YYYY-MM-DD
    const sessionDates = sessionsData.map(session => {
      return new Date(session.start_time).toISOString().split('T')[0];
    });

    // Get unique dates
    const uniqueDates = [...new Set(sessionDates)];

    // Sort dates in descending order (newest first)
    uniqueDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    // Calculate current streak
    let streak = 0;
    const today = new Date().toISOString().split('T')[0];

    // Check if there's a session today
    if (uniqueDates[0] === today) {
      streak = 1;

      // Check consecutive days before today
      for (let i = 1; i < uniqueDates.length; i++) {
        const currentDate = new Date(uniqueDates[i - 1]);
        currentDate.setDate(currentDate.getDate() - 1);
        const expectedPreviousDate = currentDate.toISOString().split('T')[0];

        if (uniqueDates[i] === expectedPreviousDate) {
          streak++;
        } else {
          break;
        }
      }
    } else {
      // Check if there was a session yesterday
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      if (uniqueDates[0] === yesterdayStr) {
        streak = 1;

        // Check consecutive days before yesterday
        for (let i = 1; i < uniqueDates.length; i++) {
          const currentDate = new Date(uniqueDates[i - 1]);
          currentDate.setDate(currentDate.getDate() - 1);
          const expectedPreviousDate = currentDate.toISOString().split('T')[0];

          if (uniqueDates[i] === expectedPreviousDate) {
            streak++;
          } else {
            break;
          }
        }
      }
    }

    return streak;
  } catch (error) {
    console.error('Error calculating writing streak:', error);
    return 0;
  }
};

/**
 * Get recent word counts for a user (last N days)
 * This is useful for calculating statistics and streaks
 */
export async function getRecentWordCounts(days: number = 7) {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Calculate date range
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);

    const { data, error } = await supabase
      .from('word_count_history')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', startDate.toISOString().split('T')[0])
      .lte('date', endDate.toISOString().split('T')[0])
      .order('date', { ascending: false });

    if (error) throw error;

    if (data && data.length > 0) {
      return data;
    } else {
      // If no data, return sample data for development
      return generateSampleWordCountData(days);
    }
  } catch (error) {
    console.error('Error fetching recent word counts:', error);
    return generateSampleWordCountData(days);
  }
}

/**
 * Generate sample word count data for development
 */
function generateSampleWordCountData(days: number) {
  interface SampleWordCountData {
    id: string;
    date: string;
    word_count: number;
    daily_change: number;
    user_id: string;
    story_id: number;
    created_at: string;
    updated_at: string;
  }

  const result: SampleWordCountData[] = [];
  const endDate = new Date();

  // Create a base value that increases each day to simulate progress
  let baseValue = 300; // Start with 300 words on the earliest day
  const dailyIncrement = 50; // Add about 50 words per day on average

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(endDate.getDate() - i);

    // Generate a word count that generally increases over time with some randomness
    // Random variation between -30 and +70 words to show realistic fluctuations
    const randomVariation = Math.floor(Math.random() * 100) - 30;
    const wordCount = Math.max(0, baseValue + randomVariation);

    // Increase the base value for the next day
    baseValue += dailyIncrement;

    result.push({
      id: `sample-${i}`,
      date: date.toISOString().split('T')[0],
      word_count: wordCount,
      daily_change: wordCount - (i < days - 1 ? (result[days - i - 2]?.word_count || 0) : 0),
      user_id: 'sample',
      story_id: 1,
      created_at: date.toISOString(),
      updated_at: date.toISOString()
    });
  }

  return result;
}

/**
 * Set a target word count for a story
 */
export async function setStoryTargetWordCount({
  storyId,
  targetWords,
  deadline
}: {
  storyId: string;
  targetWords: number;
  deadline: Date;
}): Promise<boolean> {
  try {
    // Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Check if there's already a project-type goal for this story
    const { data: existingGoals, error: checkError } = await supabase
      .from('writer_goals')
      .select('id')
      .eq('story_id', storyId)
      .eq('goal_type', 'project')
      .limit(1);

    if (checkError) {
      console.error('Error checking existing goals:', checkError);
      throw checkError;
    }

    if (existingGoals && existingGoals.length > 0) {
      // Update existing goal
      const { error: updateError } = await supabase
        .from('writer_goals')
        .update({
          target_value: targetWords,
          deadline: deadline.toISOString().split('T')[0]
        })
        .eq('id', existingGoals[0].id);

      if (updateError) {
        console.error('Error updating goal:', updateError);
        throw updateError;
      }
    } else {
      // Create new goal
      const { error: insertError } = await supabase
        .from('writer_goals')
        .insert({
          story_id: storyId,
          goal_type: 'project',
          target_value: targetWords,
          deadline: deadline.toISOString().split('T')[0],
          completed: false,
          start_date: new Date().toISOString().split('T')[0]
          // user_id and tenant_id will be set by the database trigger
        });

      if (insertError) {
        console.error('Error creating goal:', insertError);
        throw insertError;
      }
    }

    return true;
  } catch (error) {
    console.error('Error setting story target word count:', error);
    return false;
  }
}

/**
 * Update a goal's completion status
 */
export async function updateGoalProgress(id: string, completed: boolean) {
  try {
    const { data, error } = await supabase
      .from('writer_goals')
      .update({
        completed: completed,
        completed_date: completed ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating goal completion:', error);
    return null;
  }
}

/**
 * Update a goal's deadline date
 */
export async function updateGoalDeadline(id: string, deadline: string) {
  try {
    const { data, error } = await supabase
      .from('writer_goals')
      .update({
        deadline: deadline,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating goal deadline:', error);
    return null;
  }
}

/**
 * Interface for Project Chapter Statistics
 */
export interface ProjectChapterStats {
  chapterId: number;
  chapterTitle: string;
  storyId: number;
  wordCount: number;
  characterCount: number;
}

/**
 * Get aggregated chapter statistics for a project
 * Fetches all chapters and their current active word counts
 */
export async function getProjectChapterStats(projectId: string | number): Promise<ProjectChapterStats[]> {
  try {
    const numericProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;

    // Step 1: Get stories for project
    const { data: stories, error: storiesError } = await supabase
      .from('stories')
      .select('id')
      .eq('project_id', numericProjectId);

    if (storiesError) throw storiesError;
    if (!stories || stories.length === 0) return [];

    const storyIds = stories.map(s => s.id);

    // Step 2: Get chapters and their active content
    // We explicitly filter for active content in the relation
    const { data: chapters, error } = await supabase
      .from('chapters')
      .select(`
           id, 
           title, 
           story_id,
           chapter_contents(word_count, character_count, is_active)
       `)
      .in('story_id', storyIds)
      .order('id');

    if (error) throw error;

    // Transform data: find the active content for each chapter, defaulting to 0
    return (chapters || []).map(chapter => {
      // Filter for active content in case multiple were returned (though usually 1 active)
      // OR if none returned, defaults to 0
      const activeContent = Array.isArray(chapter.chapter_contents)
        ? chapter.chapter_contents.find((c: any) => c.is_active === true)
        : null;

      return {
        chapterId: chapter.id,
        chapterTitle: chapter.title,
        storyId: chapter.story_id,
        wordCount: activeContent?.word_count || 0,
        characterCount: activeContent?.character_count || 0
      };
    });
  } catch (error) {
    console.error('Error fetching project chapter stats:', error);
    return [];
  }
}

/**
 * Delete a goal
 */
export async function deleteGoal(id: string) {
  try {
    const { error } = await supabase
      .from('writer_goals')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting goal:', error);
    return false;
  }
}

// Update any remaining instances of getWritingGoals to use the new function name
export { getGoals as getWritingGoals };

export const getLongestWritingStreak = async (userId?: string, projectId?: string | number): Promise<number> => {
  try {
    // If userId is not provided, get it from the session
    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id;

      if (!userId) {
        console.error('No user logged in');
        return 0;
      }
    }

    // Get all writing sessions for this user, filtered by project if provided
    let query = supabase
      .from('writing_sessions')
      .select('start_time')
      .eq('user_id', userId)
      .order('start_time', { ascending: true });

    // Add project filter if provided
    if (projectId) {
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;
      query = query.eq('project_id', numericProjectId);
    }

    const { data: sessionsData, error } = await query;

    if (error) {
      console.error('Error fetching session data:', error);
      return 0;
    }

    if (!sessionsData || sessionsData.length === 0) {
      return 0;
    }

    // Extract dates from sessions and format to YYYY-MM-DD
    const sessionDates = sessionsData.map(session => {
      return new Date(session.start_time).toISOString().split('T')[0];
    });

    // Get unique dates
    const uniqueDates = [...new Set(sessionDates)];

    // Sort dates in ascending order (oldest first)
    uniqueDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    let longestStreak = 0;
    let currentStreak = 1;

    // Calculate longest streak by iterating through sorted unique dates
    for (let i = 1; i < uniqueDates.length; i++) {
      const currentDate = new Date(uniqueDates[i]);
      const previousDate = new Date(uniqueDates[i - 1]);

      // Check if dates are consecutive
      const diffTime = Math.abs(currentDate.getTime() - previousDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        // Consecutive day, increment current streak
        currentStreak++;
      } else {
        // Streak broken, update longest streak if current streak is longer
        longestStreak = Math.max(longestStreak, currentStreak);
        currentStreak = 1;
      }
    }

    // Check one more time after the loop in case the longest streak is the current one
    longestStreak = Math.max(longestStreak, currentStreak);

    return longestStreak;
  } catch (error) {
    console.error('Error calculating longest writing streak:', error);
    return 0;
  }
};

export const getYearlyWritingActivity = async (year?: number, userId?: string, projectId?: string | number): Promise<DailyWritingActivity[]> => {
  try {
    // If userId is not provided, get it from the session
    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id;

      if (!userId) {
        console.error('No user logged in');
        return [];
      }
    }

    // Default to current year if not specified
    const targetYear = year || new Date().getFullYear();

    // Define the date range for the target year
    const startDate = new Date(targetYear, 0, 1).toISOString(); // Jan 1
    const endDate = new Date(targetYear, 11, 31, 23, 59, 59).toISOString(); // Dec 31

    // Get all writing sessions for this user within the date range, filtered by project if provided
    let query = supabase
      .from('writing_sessions')
      .select('start_time, duration')
      .eq('user_id', userId)
      .gte('start_time', startDate)
      .lte('start_time', endDate)
      .order('start_time', { ascending: true });

    // Add project filter if provided
    if (projectId) {
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;
      query = query.eq('project_id', numericProjectId);
    }

    const { data: sessionsData, error } = await query;

    if (error) {
      console.error('Error fetching session data:', error);
      return [];
    }

    if (!sessionsData || sessionsData.length === 0) {
      return [];
    }

    // Group sessions by date (YYYY-MM-DD)
    const activityByDate: Record<string, DailyWritingActivity> = {};

    sessionsData.forEach(session => {
      const dateStr = new Date(session.start_time).toISOString().split('T')[0];

      if (!activityByDate[dateStr]) {
        activityByDate[dateStr] = {
          date: dateStr,
          sessionCount: 0,
          totalMinutes: 0
        };
      }

      activityByDate[dateStr].sessionCount += 1;
      // Convert duration from seconds to minutes
      activityByDate[dateStr].totalMinutes += Math.round((session.duration || 0) / 60);
    });

    // Convert to array
    return Object.values(activityByDate);
  } catch (error) {
    console.error('Error calculating yearly writing activity:', error);
    return [];
  }
};

export const getWritingStats = async (userId?: string, projectId?: string | number): Promise<WritingStats> => {
  try {
    // If userId is not provided, get it from the session
    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id;

      if (!userId) {
        console.error('No user logged in');
        return {
          totalSessions: 0,
          totalTimeSpent: 0,
          avgSessionDuration: 0,
          firstSessionDate: null,
          activeDays: 0,
          avgTimePerActiveDay: 0,
          dailyConsistency: 0
        };
      }
    }

    // Get all writing sessions for this user, filtered by project if provided
    let query = supabase
      .from('writing_sessions')
      .select('start_time, duration')
      .eq('user_id', userId)
      .order('start_time', { ascending: true });

    // Add project filter if provided
    if (projectId) {
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;
      query = query.eq('project_id', numericProjectId);
    }

    const { data: sessionsData, error } = await query;

    if (error) {
      console.error('Error fetching session data:', error);
      return {
        totalSessions: 0,
        totalTimeSpent: 0,
        avgSessionDuration: 0,
        firstSessionDate: null,
        activeDays: 0,
        avgTimePerActiveDay: 0,
        dailyConsistency: 0
      };
    }

    if (!sessionsData || sessionsData.length === 0) {
      return {
        totalSessions: 0,
        totalTimeSpent: 0,
        avgSessionDuration: 0,
        firstSessionDate: null,
        activeDays: 0,
        avgTimePerActiveDay: 0,
        dailyConsistency: 0
      };
    }

    // Calculate total sessions
    const totalSessions = sessionsData.length;

    // Calculate total time spent (in minutes)
    const totalTimeSpent = sessionsData.reduce(
      (total, session) => total + Math.round((session.duration || 0) / 60), // Convert seconds to minutes
      0
    );

    // Calculate average session duration (in minutes)
    const avgSessionDuration =
      totalSessions > 0 ? Math.round(totalTimeSpent / totalSessions) : 0;

    // Get first session date
    const firstSessionDate =
      sessionsData.length > 0 ? sessionsData[0].start_time : null;

    // Group sessions by day to calculate active days
    const sessionsByDay: Record<string, number> = {};
    sessionsData.forEach(session => {
      const dateStr = new Date(session.start_time).toISOString().split('T')[0];
      if (!sessionsByDay[dateStr]) {
        sessionsByDay[dateStr] = 0;
      }
      sessionsByDay[dateStr] += (session.duration || 0);
    });

    // Count active days
    const activeDays = Object.keys(sessionsByDay).length;

    // Calculate average time per active day
    const avgTimePerActiveDay = activeDays > 0 ? Math.round(totalTimeSpent / activeDays) : 0;

    // Calculate daily consistency (percentage of days with activity)
    let dailyConsistency = 0;
    if (firstSessionDate) {
      const firstDate = new Date(firstSessionDate);
      const today = new Date();
      const totalDays = Math.ceil((today.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      dailyConsistency = Math.round((activeDays / totalDays) * 100);
    }

    return {
      totalSessions,
      totalTimeSpent,
      avgSessionDuration,
      firstSessionDate,
      activeDays,
      avgTimePerActiveDay,
      dailyConsistency
    };
  } catch (error) {
    console.error('Error calculating writing stats:', error);
    return {
      totalSessions: 0,
      totalTimeSpent: 0,
      avgSessionDuration: 0,
      firstSessionDate: null,
      activeDays: 0,
      avgTimePerActiveDay: 0,
      dailyConsistency: 0
    };
  }
};

export interface OverallStats {
  totalSessions: number;
  totalTimeSpent: number;
  avgSessionDuration: number;
  firstSessionDate: string | null;
  activeDays: number;
  avgTimePerActiveDay: number;
  dailyConsistency: number;
}

export interface Streaks {
  currentStreak: number;
  longestStreak: number;
}

export interface ProjectProgressSummary {
  overall_stats: OverallStats;
  streaks: Streaks;
  activeGoals: WritingGoal[];
  recentSessions: WritingSession[];
}

/**
 * Get consolidated progress summary for a project
 */
export async function getProjectProgressSummary(projectId: string | number): Promise<ProjectProgressSummary | null> {
  try {
    const numericProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;
    const { data, error } = await supabase.rpc('get_project_progress_summary', {
      p_project_id: numericProjectId
    });

    if (error) throw error;
    return data as ProjectProgressSummary;
  } catch (error) {
    console.error('Error fetching project progress summary:', error);
    return null;
  }
}
